import { type UseMutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { ItemsApi } from '@/common/api/items';
import {
  invalidateItemsTreeSet,
  treeSetLacksItems,
} from '@/common/queries/itemsTreeSetConsistency';
import { reconcileAndSettle } from '@/common/queries/itemsTreeSetSettleState';
import { commonQueryKeys } from '@/common/queryKeys/common';
import { IntegrationItemsApi } from '@/features/settings/work-integrations/api/workIntegrationItems';

type DeleteItemVariables = { itemId: number; isOrphanedIntegrationItem?: boolean };

type DeleteItemMutationOptions = Omit<
  UseMutationOptions<void, Error, DeleteItemVariables>,
  'mutationFn'
>;

export const useDeleteItemMutation = (options?: DeleteItemMutationOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: ({ itemId, isOrphanedIntegrationItem }: DeleteItemVariables) =>
      isOrphanedIntegrationItem
        ? IntegrationItemsApi.deleteOrphanedIntegrationItem({ itemId })
        : ItemsApi.deleteItem({ itemId }),
    onSettled: async (...args) => {
      const [, error, variables] = args;
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.items.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsDataSet.all() }),
        invalidateItemsTreeSet(queryClient),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetch.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemsBulkFetchV2.all() }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.item(variables.itemId) }),
        queryClient.invalidateQueries({ queryKey: commonQueryKeys.itemDescendants.all() }),
        queryClient.invalidateQueries({ queryKey: ['itemAncestors'] }),
      ]);
      // A failed delete leaves the row in place, so there is nothing to poll for.
      if (!error) {
        void reconcileAndSettle(queryClient, [variables.itemId], {
          isReflected: treeSetLacksItems([variables.itemId]),
        });
      }
      await options?.onSettled?.(...args);
    },
  });
};
